import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Sparkles } from "lucide-react";

const HELLOASSO_WIDGET_URL =
  process.env.REACT_APP_HELLOASSO_URL ||
  "https://www.helloasso.com/associations/stered-an-noz-musique-awards/formulaires/1/widget?view=form";

const getUnitPrice = (stars) => {
  if (stars >= 10) return 2.0;
  if (stars >= 5) return 2.2;
  if (stars >= 2) return 2.35;
  return 2.5;
};

const StarSupportChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStars, setSelectedStars] = useState(1);
  const [helloAssoHeight, setHelloAssoHeight] = useState(750);
  const helloAssoIframeRef = useRef(null);

  const unitPrice = getUnitPrice(selectedStars);
  const totalPrice = (selectedStars * unitPrice).toFixed(2);

  const openChat = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleWidgetResize = (event) => {
      const iframe = helloAssoIframeRef.current;
      if (!iframe) return;

      let nextHeight = null;
      if (typeof event.data === "object" && event.data && "height" in event.data) {
        nextHeight = Number(event.data.height);
      } else if (typeof event.data === "string") {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed && "height" in parsed) {
            nextHeight = Number(parsed.height);
          }
        } catch {
          nextHeight = null;
        }
      }

      if (Number.isFinite(nextHeight) && nextHeight > 0) {
        setHelloAssoHeight((prev) => Math.max(prev, nextHeight));
      }
    };

    window.addEventListener("message", handleWidgetResize);
    return () => window.removeEventListener("message", handleWidgetResize);
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        onClick={openChat}
        className="fixed bottom-6 right-6 z-[70] group inline-flex items-center gap-3 rounded-full border border-gold/40 bg-obsidian-light/95 px-5 py-3 text-white shadow-[0_0_40px_rgba(255,215,0,0.15)] backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-[0_0_50px_rgba(255,215,0,0.25)]"
        aria-label="Ouvrir le chatbot de soutien"
      >
        <div className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-gold text-obsidian">
          <MessageCircle className="h-4 w-4" />
          <span className="absolute -top-1.5 -right-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-electric-cyan text-[10px] font-bold text-obsidian">
            +
          </span>
        </div>
        <div className="hidden sm:block text-left">
          <p className="text-xs uppercase tracking-widest text-gold">Soutenir</p>
          <p className="text-sm text-white/90">Acheter des etoiles</p>
        </div>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-end bg-obsidian/35 p-3 sm:p-6">
          <div className="w-full max-w-[420px] overflow-hidden rounded-2xl border border-white/10 bg-obsidian-light shadow-2xl">
            <div className="relative border-b border-white/10 bg-gradient-to-r from-gold/20 via-gold/10 to-electric-cyan/15 px-4 py-4">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-gold/50 hover:text-white"
                aria-label="Fermer le chatbot"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="text-[11px] uppercase tracking-[0.24em] text-white/60">Assistant Stered</p>
              <h3 className="mt-1 font-syne text-xl font-bold text-white">Soutien de l'association</h3>
              <p className="text-sm text-white/70">Paiement securise directement via HelloAsso.</p>
            </div>

            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4 py-5">
              <div className="rounded-xl border border-gold/30 bg-gold/10 p-3 text-white">
                <p className="text-[11px] uppercase tracking-wider text-gold/90">Choix des etoiles</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold/60"
                    onClick={() => setSelectedStars((prev) => Math.max(1, prev - 1))}
                    aria-label="Retirer une etoile"
                  >
                    -
                  </button>
                  <p className="text-center text-sm">
                    <span className="font-semibold text-gold">{selectedStars}</span> etoile{selectedStars > 1 ? "s" : ""}
                  </p>
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold/60"
                    onClick={() => setSelectedStars((prev) => Math.min(100, prev + 1))}
                    aria-label="Ajouter une etoile"
                  >
                    +
                  </button>
                </div>
                <p className="mt-2 text-xs text-white/80">
                  Tarif unitaire: {unitPrice.toFixed(2)} EUR - Total estime: {totalPrice} EUR
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-obsidian/50 p-3 text-sm text-white/90">
                <p className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  Choisissez le nombre d'etoiles ici, puis finalisez le paiement dans HelloAsso. Les donnees sont synchronisees automatiquement avec notre backend.
                </p>
              </div>

              {HELLOASSO_WIDGET_URL && (
                <div className="space-y-2 rounded-xl border border-electric-cyan/30 bg-electric-cyan/5 p-2">
                  <p className="px-2 text-[11px] uppercase tracking-wider text-electric-cyan/80">Paiement securise HelloAsso</p>
                  <iframe
                    id="haWidgetLight"
                    ref={helloAssoIframeRef}
                    title="Widget HelloAsso"
                    allow="payment"
                    allowTransparency
                    scrolling="auto"
                    src={HELLOASSO_WIDGET_URL}
                    style={{ width: "100%", height: `${helloAssoHeight}px`, border: "none" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StarSupportChatbot;
